import { Box3 } from 'three';
import { BuildingRegistry } from '../world/BuildingRegistry';
import { Player } from '../player/Player';

/**
 * Resolves the player (modelled as an axis-aligned box) against the street and
 * every building box, using minimum-translation resolution. As a side effect it
 * reports whether the player is grounded and, if touching a vertical face, the
 * outward wall normal and that building's roof height — the data the climbing
 * system needs.
 */
export class AABBCollider {
  constructor(private readonly registry: BuildingRegistry) {}

  resolve(player: Player): void {
    player.grounded = false;
    player.touchingWall = false;

    this.resolveGround(player);
    for (const box of this.registry.boxes) this.resolveBox(player, box);
  }

  private resolveGround(player: Player): void {
    if (player.position.y <= 0) {
      player.position.y = 0;
      if (player.velocity.y < 0) player.velocity.y = 0;
      player.grounded = true;
    }
  }

  private resolveBox(player: Player, box: Box3): void {
    const r = player.radius;
    const h = player.height;
    const p = player.position;

    // Broad-phase rejection.
    if (p.x + r < box.min.x || p.x - r > box.max.x) return;
    if (p.z + r < box.min.z || p.z - r > box.max.z) return;
    if (p.y + h < box.min.y || p.y > box.max.y) return;

    // Penetration depth along each candidate push direction.
    const pen = {
      px: box.max.x - (p.x - r),
      nx: p.x + r - box.min.x,
      pz: box.max.z - (p.z - r),
      nz: p.z + r - box.min.z,
      py: box.max.y - p.y, // push up onto the roof
      ny: p.y + h - box.min.y, // push down below
    };

    // Resolve along the axis of least penetration.
    let axis: keyof typeof pen = 'px';
    for (const key of ['nx', 'pz', 'nz', 'py', 'ny'] as const) {
      if (pen[key] < pen[axis]) axis = key;
    }

    switch (axis) {
      case 'px':
        p.x = box.max.x + r;
        if (player.velocity.x < 0) player.velocity.x = 0;
        this.markWall(player, 1, 0, box.max.y);
        break;
      case 'nx':
        p.x = box.min.x - r;
        if (player.velocity.x > 0) player.velocity.x = 0;
        this.markWall(player, -1, 0, box.max.y);
        break;
      case 'pz':
        p.z = box.max.z + r;
        if (player.velocity.z < 0) player.velocity.z = 0;
        this.markWall(player, 0, 1, box.max.y);
        break;
      case 'nz':
        p.z = box.min.z - r;
        if (player.velocity.z > 0) player.velocity.z = 0;
        this.markWall(player, 0, -1, box.max.y);
        break;
      case 'py':
        p.y = box.max.y;
        if (player.velocity.y < 0) player.velocity.y = 0;
        player.grounded = true;
        break;
      case 'ny':
        p.y = box.min.y - h;
        if (player.velocity.y > 0) player.velocity.y = 0;
        break;
    }
  }

  private markWall(player: Player, nx: number, nz: number, topY: number): void {
    player.touchingWall = true;
    player.wallNormal.set(nx, 0, nz);
    player.wallTopY = topY;
  }
}
